import React, { useRef, useState } from "react"
import styled from "@emotion/styled"
import Image from "src/components/Image"
import Button from "src/components/Button"
import Section from "src/components/Section"
import Grid from "src/components/Grid"
import Slider from "react-slick"
import { typography, colors, animations, mq, globals } from "src/styles"

const Wrapper = styled(Section)`
${({ vertical }) =>
`
${globals.verticalSpacing("padding-top", vertical)}
${globals.verticalSpacing("padding-bottom", vertical)}
`}
${({ horizontal }) =>
`
${globals.verticalSpacing("padding-left", horizontal)}
${globals.verticalSpacing("padding-right", horizontal)}
`}
`

const SliderControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  ${({ postCount }) => `
    ${postCount > 5 ? "display: block;" : "display: none;"}
    ${mq.largerAndBelow} {
      ${postCount > 4 ? "display: block;" : "display: none;"}
    }
    ${mq.largeAndBelow} {
      ${postCount > 3 ? "display: block;" : "display: none;"}
    }
    ${mq.mediumAndBelow} {
      ${postCount > 2 ? "display: block;" : "display: none;"}
    }
    ${mq.smallAndBelow} {
      ${postCount > 1 ? "display: block;" : "display: none;"}
    }
  `}
  > button:first-of-type {
    margin-right: 10px;
  }
`

const IntroWrap = styled.div`
  ${globals.verticalSpacing("margin-bottom", 0.5)}
  display: flex;
  align-items: center;
  justify-content: space-between;
  h3 {
    margin: 0;
  }
  ${SliderControls} {
    ${mq.mediumAndBelow} {
      display: none;
    }
  }
`

const MobileControls = styled(Grid)`
  ${globals.verticalSpacing("margin-top", 0.5)}
  ${mq.largeAndUp} {
    display: none;
  }
`

const Slide = styled.div`
  padding: 10px;
  height: 100%;
  ${mq.mediumAndBelow} {
    padding: 10px 0 10px 20px;
  }
  ${({ firstSlide }) =>
    firstSlide
      ? `
    padding-left: var(--site-margins);
    ${mq.mediumAndUp} {
      padding-left: ${100 / 14}vw;
    }
    ${mq.largerAndUp} {
      padding-left: calc(${100 / 14}vw + 15px);
    }
  `
      : ""}
  ${({ lastSlide }) =>
    lastSlide
      ? `
    padding-right: var(--site-margins);
    ${mq.mediumAndUp} {
      padding-right: ${100 / 14}vw;
    }
    ${mq.largerAndUp} {
      padding-right: calc(${100 / 14}vw + 15px);
    }
  `
      : ""}
`

const StyledSlider = styled(Slider)`
  width: 100%;
  max-width: 100%;
  .slick-track {
    display: flex;
    align-items: stretch;
    .slick-slide {
      float: none !important;
      height: auto;
      flex-shrink: 0;
      flex-grow: 0;
      > div {
        height: 100%;
      }
    }
  }
  > div {
    > a {
      height: 100%;
    }
  }
`

const ThumbnailInfo = styled.div`
  p {
    transition: color ${animations.mediumSpeed} ease-in-out;
  }
  p.name {
    margin: .75em 0 0;
    ${typography.body}
    color: ${colors.textColor};
    line-height: 1.4em;
    ${mq.mediumAndBelow} {
      ${typography.bodyMedium}
      line-height: 1.4em;
    }
  }
  p.title {
    margin: 0;
    ${typography.bodySmall}
    line-height: 1.4em;
  }
`

const ThumbnailImage = styled(Image)`
  &:after {
    content: "";
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: black;
    opacity: 0;
    transition: opacity ${animations.mediumSpeed} ease-in-out;
    border-radius: var(--card-border-radius);
  }
`

const ThumbnailItem = styled.div`
  display: block;
  width: calc((100vw - 20px * 5.5 - (100vw / 7)) / 5);
  ${mq.largerAndBelow} {
    width: calc((100vw - 20px * 4.5 - (100vw / 7)) / 4);
  }
  ${mq.largeAndBelow} {
    width: calc((100vw - 20px * 4.5 - (100vw / 7)) / 3);
  }
  ${mq.mediumAndBelow} {
    width: 40vw;
  }
  ${mq.smallAndBelow} {
    width: 80vw;
  }
  ${({ hasBio }) =>
    hasBio
      ? `
    &:hover {
      ${ThumbnailInfo} {
        p.name {
          color: var(--main-color);
        }
        p.title {
          color: var(--text-color);
        }
      }
      ${ThumbnailImage} {
        &:after {
          opacity: .2;
        }
      }
    }
  `
      : ""}
`

const TeamMember = ({ member, toggleModal }) => {
  const onClick = () => {
    if (member?._rawBio) {
      toggleModal(member._key + "-team-modal")
    } else {
      return false
    }
  }
  return (
    <ThumbnailItem
      onClick={onClick}
      hasBio={member?._rawBio}
      as={member?._rawBio ? "a" : "div"}
    >
      {member?.headshot?.asset?.gatsbyImageData && (
        <ThumbnailImage
          rounded
          image={member.headshot.asset.gatsbyImageData}
          ratio={1}
          loading="eager"
        />
      )}
      <ThumbnailInfo>
        <p className="name">{member.name}</p>
        <p className="title">{member.jobTitle}</p>
      </ThumbnailInfo>
    </ThumbnailItem>
  )
}

const TeamSlider = ({
  teamMembers,
  toggleModal,
  prevTheme,
  theme,
  nextTheme,
  isFirstSection,
  id,
  headingSize,
  headline,
  vertical,
  horizontal
}) => {
  const slider = useRef(null)
  const [activeSlide, setActiveSlide] = useState(0)
  const settings = {
    arrows: false,
    slidesToShow: teamMembers.length <= 5 ? teamMembers.length : 5,
    infinite: false,
    variableWidth: true,
    centerMode: false,
    swipe: true,
    speed: 500,
    beforeChange: (oldIndex, newIndex) => {
      setActiveSlide({ old: oldIndex, new: newIndex })
    },
    responsive: [
      {
        breakpoint: mq.largerBreakpoint,
        settings: {
          slidesToShow: teamMembers.length <= 4 ? teamMembers.length : 4,
        },
      },
      {
        breakpoint: mq.largeBreakpoint,
        settings: {
          slidesToShow: teamMembers.length <= 3 ? teamMembers.length : 3,
        },
      },
      {
        breakpoint: mq.mediumBreakpoint,
        settings: {
          slidesToShow: teamMembers.length <= 2 ? teamMembers.length : 2,
        },
      },
      {
        breakpoint: mq.smallBreakpoint,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  }

  const prevDisabled =
    activeSlide.old === activeSlide.new &&
    activeSlide.old === 0 &&
    activeSlide.new === 0
  const nextDisabled =
    activeSlide.old === activeSlide.new &&
    activeSlide.old !== 0 &&
    activeSlide.new !== 0

  return (
    <Wrapper
      prevTheme={prevTheme}
      setTheme={theme}
      nextTheme={nextTheme}
      isFirstSection={isFirstSection}
      id={id}
      fullWidth
      vertical={vertical}
      horizontal={horizontal}
    >
      <Grid small="m [1] m" medium="1 [12] 1" large="m 1 [12] 1 m">
        <IntroWrap>
          <h3 className={headingSize ? headingSize : 'h3'}>{headline || "Our Team"}</h3>
          <SliderControls postCount={teamMembers?.length}>
            <Button
              icon="arrow_back"
              onClick={() => slider?.current?.slickPrev()}
              shape="circle"
              title="Go to previous slide"
              setTheme="white"
            />
            <Button
              icon="arrow_forward"
              onClick={() => slider?.current?.slickNext()}
              shape="circle"
              title="Go to next slide"
              setTheme="white"
            />
          </SliderControls>
        </IntroWrap>
      </Grid>
      <StyledSlider ref={slider} {...settings}>
        {teamMembers?.length > 0 &&
          teamMembers.map((member, index) => (
            <Slide
              key={member._key}
              firstSlide={index === 0}
              lastSlide={index === teamMembers.length - 1}
            >
              <TeamMember
                member={member}
                key={member._key}
                toggleModal={toggleModal}
              />
            </Slide>
          ))}
      </StyledSlider>
      <MobileControls small="m [1] m" medium="1 [12] 1" large="m 1 [12] 1 m">
        <SliderControls postCount={teamMembers?.length}>
          <Button
            icon="arrow_back"
            onClick={() => slider?.current?.slickPrev()}
            shape="circle"
            title="Go to previous slide"
            setTheme="white"
            disabled={prevDisabled}
          />
          <Button
            icon="arrow_forward"
            onClick={() => slider?.current?.slickNext()}
            shape="circle"
            title="Go to next slide"
            setTheme="white"
            disabled={nextDisabled}
          />
        </SliderControls>
      </MobileControls>
    </Wrapper>
  )
}

export default TeamSlider
